import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
} from "reactstrap"
import axios from "axios"
import apiURL from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const LeadsSettings = () => {
  document.title = "Settings | CRM"

  // Accordion state
  const [openAccordion, setOpenAccordion] = useState("1")
  const toggleAccordion = id => setOpenAccordion(openAccordion === id ? "" : id)

  // Status management states
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingStatus, setEditingStatus] = useState(null)
  const [form, setForm] = useState({ status_name: "", color: "#007BFF" })

  // Fetch all statuses
  const fetchStatuses = async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`${apiURL}/settings/statuses`)
      if (data.status === "success") setStatuses(data.data)
    } catch (err) {
      console.error("Error fetching statuses:", err)
      toastr.error("Failed to fetch statuses")
    } finally {
      setLoading(false)
    }
  }

  // Add or update status
  const handleSave = async () => {
    if (!form.status_name.trim()) {
      toastr.error("Status name is required")
      return
    }

    try {
      if (editingStatus) {
        await axios.put(`${apiURL}/settings/statuses/${editingStatus.id}`, form)
        toastr.success("Status updated successfully")
      } else {
        await axios.post(`${apiURL}/settings/statuses`, form)
        toastr.success("Status added successfully")
      }

      setModalOpen(false)
      setForm({ status_name: "", color: "#007BFF" })
      setEditingStatus(null)
      fetchStatuses()
    } catch (err) {
      console.error("Error saving status:", err)
      toastr.error("Error saving status")
    }
  }

  // Delete status
  const handleDelete = async id => {
    if (!window.confirm("Are you sure you want to delete this status?")) return
    try {
      await axios.delete(`${apiURL}/settings/statuses/${id}`)
      toastr.success("Status deleted successfully")
      fetchStatuses()
    } catch (err) {
      toastr.error("Error deleting status")
    }
  }

  // Fetch on mount
  useEffect(() => {
    fetchStatuses()
  }, [])

  return (
    <div className="page-content">
      <Card className="shadow-sm border-0">
        <CardBody>
          <h4 className="fw-semibold mb-3">System Settings</h4>

          <Accordion open={openAccordion} toggle={toggleAccordion}>
            {/* ========== Lead Status Management Section ========== */}
            <AccordionItem>
              <AccordionHeader
                targetId="1"
                className="fw-semibold bg-light rounded-3"
              >
                Lead Status Management
              </AccordionHeader>

              <AccordionBody accordionId="1">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0">Manage Lead Statuses</h6>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => {
                      setEditingStatus(null)
                      setForm({ status_name: "", color: "#007BFF" })
                      setModalOpen(true)
                    }}
                  >
                    + Add Status
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center p-4">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <Table bordered hover responsive>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Status Name</th>
                        <th>Color Preview</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statuses.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No statuses found.
                          </td>
                        </tr>
                      ) : (
                        statuses.map((s, i) => (
                          <tr key={s.id}>
                            <td>{i + 1}</td>
                            <td className="text-capitalize">{s.status_name}</td>
                            <td>
                              <span
                                className="badge rounded-pill px-3"
                                style={{
                                  backgroundColor: s.color || "#6c757d",
                                  color: "#fff",
                                }}
                              >
                                {s.color}
                              </span>
                            </td>
                            <td>
                              <Button
                                size="md"
                                color="link"
                                className="text-success"
                                onClick={() => {
                                  setEditingStatus(s)
                                  setForm({
                                    status_name: s.status_name,
                                    color: s.color,
                                  })
                                  setModalOpen(true)
                                }}
                              >
                                <i className="bx bx-edit"></i>
                              </Button>
                              <Button
                                size="md"
                                color="link"
                                className="text-danger"
                                onClick={() => handleDelete(s.id)}
                              >
                                <i className="bx bx-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </AccordionBody>
            </AccordionItem>

            {/* ========== Placeholder for Future Settings ========== */}
            <AccordionItem>
              <AccordionHeader
                targetId="2"
                className="fw-semibold bg-light rounded-3"
              >
                Other Settings (Coming Soon)
              </AccordionHeader>
              <AccordionBody accordionId="2">
                <p className="text-muted mb-0">Coming Soon</p>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
        </CardBody>
      </Card>

      {/* ========== Add / Edit Modal ========== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingStatus ? "Edit Status" : "Add New Status"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Status Name</Label>
            <Input
              type="text"
              value={form.status_name}
              onChange={e => setForm({ ...form, status_name: e.target.value })}
              placeholder="Enter status name"
            />
          </FormGroup>

          <FormGroup>
            <Label>Badge Color</Label>
            <div className="d-flex align-items-center gap-3">
              <Input
                type="color"
                value={form.color}
                onChange={e => setForm({ ...form, color: e.target.value })}
                style={{ width: "100px", height: "25px", padding: "2px" }}
              />
              <span
                className="badge rounded-pill px-3"
                style={{ backgroundColor: form.color, color: "#fff" }}
              >
                {form.color}
              </span>
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" className="btn btn-sm" onClick={handleSave}>
            {editingStatus ? "Update" : "Add"}
          </Button>
          <Button
            color="secondary"
            className="btn btn-sm"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default LeadsSettings
